import React from "react";
import Background from "../Background";
import ContainerEffectOverlay from "../ContainerEffectOverlay";
import { SlotRenderer, useHeroAnimation, useHeroAmbient } from "./heroModeUtils";
import "./heroModeAnimations.css";

const BP_CONFIG = {
  extra_large: { bodyMaxWidth: 640, vertPadding: 80 },
  large:       { bodyMaxWidth: 600, vertPadding: 64 },
  medium:      { bodyMaxWidth: 560, vertPadding: 48 },
  small:       { bodyMaxWidth: "calc(100% - 48px)", vertPadding: 40 },
  extra_small: { bodyMaxWidth: "calc(100% - 32px)", vertPadding: 32 },
};

const HeroCentered = ({ item, heroBase, heroSlots }) => {
  const {
    heroRootRef, containerClasses, containerStyle, fullBleedStyle,
    contentInsetStyle, responsiveHeight, baseHeightPx, bp,
  } = heroBase;

  const isAnimated = heroSlots?.isAnimated || false;
  const config = BP_CONFIG[bp] || BP_CONFIG.medium;

  const entrance = heroSlots?.animatedEntrance || "scale-in";
  // Centered's ambient animation (pulse, shipped with
  // ``centered_animated``) attaches to the whole content column,
  // giving the typographic hero a gentle breath while staying out of
  // the way of the main text. Ambient is mode-owned, independent of
  // the entrance toggle.
  const contentAmbientClass = useHeroAmbient(heroSlots?.ambientAnimation);

  const headingAnim = useHeroAnimation(isAnimated, entrance, { delay: 0 });
  const bodyAnim = useHeroAnimation(isAnimated, entrance, { delay: 150 });
  const ctaAnim = useHeroAnimation(isAnimated, entrance, { delay: 300 });

  const bodyMaxWidth =
    typeof config.bodyMaxWidth === "number"
      ? `${config.bodyMaxWidth}px`
      : config.bodyMaxWidth;

  return (
    <div
      ref={heroRootRef}
      data-hero-root="true"
      className={containerClasses}
      style={{ ...containerStyle, ...fullBleedStyle }}
    >
      <Background
        bgImage={heroBase.bgImage}
        imageStyleClasses={heroBase.imageStyleClasses}
        imageInlineStyles={heroBase.imageInlineStyles}
        colorClass={heroBase.colorClass}
        colorStyle={heroBase.colorStyle}
        jsPatternLayer={heroBase.jsPatternLayer}
      />
      <ContainerEffectOverlay data={heroBase.data} />

      <div
        className={
          responsiveHeight
            ? "relative z-10 flex items-center justify-center w-full"
            : "absolute inset-0 z-10 flex items-center justify-center"
        }
        style={responsiveHeight ? { minHeight: `${baseHeightPx}px` } : undefined}
      >
        <div
          className={`flex flex-col items-center justify-center text-center gap-5 w-full ${contentAmbientClass}`}
          style={{
            paddingTop: config.vertPadding,
            paddingBottom: config.vertPadding,
            ...contentInsetStyle,
          }}
        >
          <div ref={headingAnim.ref} className={`w-full ${headingAnim.className}`} style={headingAnim.style}>
            <SlotRenderer
              slotName="heading"
              heroSlots={heroSlots}
              heroBase={heroBase}
              heroId={item?.id}
              slotIndex={0}
            />
          </div>

          <div
            ref={bodyAnim.ref}
            className={`mx-auto ${bodyAnim.className}`}
            style={{ maxWidth: bodyMaxWidth, ...bodyAnim.style }}
          >
            <SlotRenderer
              slotName="body"
              heroSlots={heroSlots}
              heroBase={heroBase}
              heroId={item?.id}
              slotIndex={1}
            />
          </div>

          <div ref={ctaAnim.ref} className={ctaAnim.className} style={ctaAnim.style}>
            <SlotRenderer
              slotName="cta"
              heroSlots={heroSlots}
              heroBase={heroBase}
              heroId={item?.id}
              slotIndex={2}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(HeroCentered);
